import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { ArtistWidget } from '@eventbrite/artist-widget';
import { track } from '@eventbrite/legacy-infra.datalayer-library';
import { defineMessage, MessageDescriptor } from 'react-intl';
import { LISTING_HEAP_IDS, VIEW_ALL_MESSAGE } from '@/components/_shared/constants';
import { useIntl } from 'react-intl';
import { getImageProps } from 'next/image';

interface Props {
  info: ArtistInfo;
}

export const ArtistsInfo: React.FC<Props> = ({ info }) => {
  const { artistType, artists } = info;

  const artistsWithOptimizedImages = artists.map((artist) => ({
    ...artist,
    imageUrl: artist.imageUrl
      ? getImageProps({ alt: '', width: 56, height: 56, src: artist.imageUrl }).props.src
      : undefined,
    fullSizeImageUrl: artist.fullSizeImageUrl
      ? getImageProps({ alt: '', width: 430, height: 430, src: artist.fullSizeImageUrl }).props.src
      : undefined,
  }));

  const { formatMessage } = useIntl();
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      track({ eventName: LISTING_HEAP_IDS.ARTIST_WIDGET_VIEW, eventData: { artistType } });
    }
  }, [inView, artistType]);

  return (
    <div ref={ref}>
      <ArtistWidget
        artistData={{
          artistType: formatMessage(config[artistType].label),
          artistRole: formatMessage(config[artistType].role),
          artistList: artistsWithOptimizedImages,
        }}
        translatedTexts={{
          VIEW_LESS: formatMessage(defineMessage({ id: 'View less', defaultMessage: 'View less' })),
          VIEW_ALL: formatMessage(VIEW_ALL_MESSAGE),
        }}
      />
    </div>
  );
};

const config: { [key: string]: { label: MessageDescriptor; role: MessageDescriptor } } = {
  lineup: {
    label: defineMessage({ id: 'Lineup', defaultMessage: 'Lineup' }),
    role: defineMessage({ id: 'Headliner', defaultMessage: 'Headliner' }),
  },
  artists: {
    label: defineMessage({ id: 'Artists', defaultMessage: 'Artists' }),
    role: defineMessage({ id: 'Featured', defaultMessage: 'Featured' }),
  },
  speakers: {
    label: defineMessage({ id: 'Speakers', defaultMessage: 'Speakers' }),
    role: defineMessage({ id: 'Keynote', defaultMessage: 'Keynote' }),
  },
  performers: {
    label: defineMessage({ id: 'Performers', defaultMessage: 'Performers' }),
    role: defineMessage({ id: 'Headliner', defaultMessage: 'Headliner' }),
  },
};

export interface ArtistInfo {
  artistType: 'lineup' | 'artists' | 'speakers' | 'performers';
  artists: Artist[];
}

interface Artist {
  id: string;
  name: string;
  tagline?: string;
  imageUrl?: string;
  fullSizeImageUrl?: string;
  role?: string;
  socialMedia?: SocialMedia[];
  bio?: string;
}

interface SocialMedia {
  linkType: string;
  url: string;
}
